import React, { useEffect, useState } from "react";
import { Loader2, Home, Wrench, Wallet, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const rupees = (p) => `₹${((p || 0) / 100).toLocaleString("en-IN")}`;

export default function Property() {
  const { user } = useAuth();
  const [tab, setTab] = useState("tenants");
  const [tenants, setTenants] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tenantForm, setTenantForm] = useState({ user_id: "", unit_label: "", monthly_rent: "" });
  const [ticketForm, setTicketForm] = useState({ owner_id: "", unit_label: "", subject: "", body: "", priority: "medium" });

  const load = async () => {
    try {
      const [t, k] = await Promise.all([
        api.get("/property/tenants"),
        api.get("/property/tickets?role=owner"),
      ]);
      setTenants(t.data); setTickets(k.data);
    } catch {}
  };
  useEffect(() => { if (user) load(); }, [user]);

  const addTenant = async () => {
    if (!tenantForm.user_id || !tenantForm.unit_label || !(+tenantForm.monthly_rent > 0)) { toast.error("Fill all fields"); return; }
    try {
      await api.post("/property/tenants", {
        user_id: tenantForm.user_id, unit_label: tenantForm.unit_label,
        monthly_rent_paise: Math.round(+tenantForm.monthly_rent * 100),
      });
      toast.success("Tenant added");
      setTenantForm({ user_id: "", unit_label: "", monthly_rent: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const raiseTicket = async () => {
    if (!ticketForm.owner_id || !ticketForm.subject) { toast.error("Owner id + subject required"); return; }
    try {
      await api.post("/property/tickets", ticketForm);
      toast.success("Ticket raised");
      setTicketForm({ owner_id: "", unit_label: "", subject: "", body: "", priority: "medium" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateTicket = async (id, status) => {
    try { await api.post(`/property/tickets/${id}/status`, { status }); toast.success(status); load(); }
    catch { toast.error("Failed"); }
  };

  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to manage property.</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-4" data-testid="property-page">
      <div className="flex items-center gap-2 mb-1"><Home className="text-[#E05E36]" size={22} />
        <h1 className="heading text-2xl font-black text-stone-900">Property</h1></div>
      <p className="text-xs text-stone-500 mb-3">Tenants, rent collection, and maintenance tickets in one place.</p>

      <div className="flex gap-2 mb-3">
        {["tenants", "tickets"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-xs font-bold rounded-full ${tab === t ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}
            data-testid={`property-tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === "tenants" && (
        <>
          <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-2 mb-3">
            <div className="font-bold text-sm">Add tenant</div>
            <Input placeholder="Tenant user_id" value={tenantForm.user_id} onChange={e => setTenantForm({ ...tenantForm, user_id: e.target.value })} data-testid="prop-tenant-userid" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Unit label (A-101)" value={tenantForm.unit_label} onChange={e => setTenantForm({ ...tenantForm, unit_label: e.target.value })} data-testid="prop-tenant-unit" />
              <Input placeholder="Monthly rent ₹" type="number" value={tenantForm.monthly_rent} onChange={e => setTenantForm({ ...tenantForm, monthly_rent: e.target.value })} data-testid="prop-tenant-rent" />
            </div>
            <Button onClick={addTenant} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="prop-tenant-add"><Plus size={12} className="mr-1" /> Add</Button>
          </div>

          {tenants.length === 0 ? <div className="text-center py-8 text-stone-500 text-xs">No tenants yet.</div> :
            <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
              {tenants.map(t => (
                <div key={t.tenant_id} className="p-3 flex items-center justify-between" data-testid={`prop-tenant-${t.tenant_id}`}>
                  <div>
                    <div className="font-bold text-sm">{t.unit_label}</div>
                    <div className="text-[11px] font-mono text-stone-500">{t.user_id.slice(0, 16)}</div>
                  </div>
                  <div className="text-right text-sm font-black text-[#E05E36]">{rupees(t.monthly_rent_paise)}<span className="text-[10px] text-stone-500 font-normal">/mo</span></div>
                </div>
              ))}
            </div>
          }
        </>
      )}

      {tab === "tickets" && (
        <>
          <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-2 mb-3">
            <div className="font-bold text-sm">Raise maintenance ticket</div>
            <Input placeholder="Owner user_id (property owner)" value={ticketForm.owner_id} onChange={e => setTicketForm({ ...ticketForm, owner_id: e.target.value })} data-testid="prop-ticket-owner" />
            <Input placeholder="Unit label" value={ticketForm.unit_label} onChange={e => setTicketForm({ ...ticketForm, unit_label: e.target.value })} data-testid="prop-ticket-unit" />
            <Input placeholder="Subject" value={ticketForm.subject} onChange={e => setTicketForm({ ...ticketForm, subject: e.target.value })} data-testid="prop-ticket-subject" />
            <Textarea placeholder="Details" rows={2} value={ticketForm.body} onChange={e => setTicketForm({ ...ticketForm, body: e.target.value })} />
            <select value={ticketForm.priority} onChange={e => setTicketForm({ ...ticketForm, priority: e.target.value })} className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select>
            <Button onClick={raiseTicket} className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="prop-ticket-raise"><Wrench size={12} className="mr-1" /> Raise ticket</Button>
          </div>

          {tickets.length === 0 ? <div className="text-center py-8 text-stone-500 text-xs">Inbox is empty.</div> :
            <div className="space-y-2">
              {tickets.map(t => (
                <div key={t.ticket_id} className="bg-white border border-stone-200 rounded-2xl p-3" data-testid={`prop-ticket-${t.ticket_id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded bg-stone-100 text-stone-700`}>{t.status}</span>
                    <span className="text-[10px] font-bold text-red-500">{t.priority}</span>
                    <span className="text-[10px] text-stone-500">{t.unit_label}</span>
                  </div>
                  <div className="font-bold text-sm">{t.subject}</div>
                  <div className="text-xs text-stone-600 line-clamp-2">{t.body}</div>
                  <div className="mt-2 flex gap-1.5">
                    {t.status !== "resolved" && <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => updateTicket(t.ticket_id, "resolved")} data-testid={`prop-ticket-resolve-${t.ticket_id}`}>Resolve</Button>}
                    {t.status !== "closed" && <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => updateTicket(t.ticket_id, "closed")} data-testid={`prop-ticket-close-${t.ticket_id}`}>Close</Button>}
                  </div>
                </div>
              ))}
            </div>
          }
        </>
      )}
    </div>
  );
}
